import { useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Badge, Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Stack, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import type {
  DanieDoPotwierdzenia, FiltrHistoriiModeracji, PropozycjaZmianyDania, RodzajDecyzjiModeracji, StronaPropozycji,
} from '@/lib/api/analytics';
import { opisBleduOperacji, powodBleduOdczytu } from '@/lib/administracja/komunikaty';
import { useAuth } from '@/lib/auth/AuthContext';
import { formatGrosze } from '@/lib/format';
import {
  decyzjaOperatora, etykietaDecyzjiAi, etykietaRozstrzygniecia, FILTRY_HISTORII, opisRekoncyliacji, zakresWynikow,
} from '@/lib/moderacja/historia';
import {
  cenaZlote, czyProblemZdjecia, etykietaOczekiwania, etykietaPowiadomienia, etykietaRodzaju, etykietaStanuDania,
  kategoriaPowodu, opisPowodu, opisZdjecia, opisZmiany, pewnoscProcent, ROZMIAR_STRONY_MODERACJI, RODZAJ_NOWE_DANIE,
  wolnoZatwierdzic, znacznikiRyzyka,
} from '@/lib/moderacja/kolejka';
import {
  odswiezModeracje, useAnalyticsApi, useAudytModeracji, useDaniaDoPotwierdzenia, useHistoriaModeracji,
  useKolejkaModeracji, useLicznikiModeracji, useZatwierdzonePrzezAi,
} from '@/lib/useAnalytics';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * ADMINISTRACJA → MODERACJA MENU (OWNER 2026-10-10)
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Wyrocznia: stary panel admina → „Menu" (/temp-menu, tuszama-panel origin/main 6b6a59a7).
 * Cztery zakładki, te same wywołania systemu głównego, te same statusy — bez nowych:
 *   • Do potwierdzenia                 — dania nieopublikowane (legacy): Zatwierdź / Odrzuć;
 *   • Do ręcznego zatwierdzenia (AI)   — propozycje zmian skierowane przez AI do człowieka;
 *   • Zatwierdzone przez AI            — historia decyzji automatu;
 *   • Historia                         — wszystko rozstrzygnięte, z filtrem systemu głównego.
 * Po każdej decyzji wszystkie zakładki i liczniki czytają się ponownie — stan końcowy pochodzi
 * z systemu głównego, nie z założenia. Dawny ekran CC (replika `crm_dishes`, bez propozycji AI
 * i bez podpiętych przycisków) jest tym ZASTĄPIONY.
 */

type Zakladka = 'do-potwierdzenia' | 'kolejka' | 'ai' | 'historia';
const ZAKLADKI: readonly Zakladka[] = ['do-potwierdzenia', 'kolejka', 'ai', 'historia'];

const dataGodzina = (iso: string | null) => (iso
  ? new Date(iso).toLocaleString('pl-PL', { timeZone: 'Europe/Warsaw', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—');

const KOLOR_KATEGORII: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
  IMAGE: 'error', TEXT: 'error', DUPLICATE: 'warning', PRICE: 'warning', FORMAT: 'info', LOW_CONF: 'info', OTHER: 'default', BRAK: 'default',
};

function Miniatura({ url, alt }: { url: string | null; alt: string }) {
  if (!url) return <Typography variant="caption" sx={{ color: 'text.secondary' }}>brak</Typography>;
  return <Box component="img" src={url} alt={alt} loading="lazy" sx={{ maxHeight: 60, maxWidth: 90, borderRadius: 1, display: 'block' }} />;
}

function Danie({ p }: { p: PropozycjaZmianyDania }) {
  return (
    <>
      <div>{p.newName ?? p.mealName ?? '—'}</div>
      {p.oldName && p.oldName !== p.newName ? <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>było: {p.oldName}</Typography> : null}
    </>
  );
}

function RodzajZmiany({ p }: { p: PropozycjaZmianyDania }) {
  return (
    <>
      <div>{etykietaRodzaju(p.kind)}</div>
      <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>{opisZmiany(p)}</Typography>
    </>
  );
}

const cenaPrzedPo = (p: PropozycjaZmianyDania) => `${p.kind === RODZAJ_NOWE_DANIE ? '—' : cenaZlote(p.oldPrice)} → ${cenaZlote(p.newPrice)}`;

function Stronicowanie({ strona, dane, onStrona }: { strona: number; dane: StronaPropozycji; onStrona: (s: number) => void }) {
  return (
    <TablePagination
      component="div"
      count={dane.wszystkich}
      page={Math.max(0, strona - 1)}
      rowsPerPage={ROZMIAR_STRONY_MODERACJI}
      rowsPerPageOptions={[ROZMIAR_STRONY_MODERACJI]}
      onPageChange={(_, p) => onStrona(p + 1)}
      labelDisplayedRows={() => zakresWynikow(strona, ROZMIAR_STRONY_MODERACJI, dane.wszystkich)}
      labelRowsPerPage="Na stronę"
    />
  );
}

/** Stan zakładki: ładowanie / błąd (nie „brak danych") / pusto / treść. */
function StanZakladki({ ladowanie, blad, pusto, pustoTytul, children }: {
  ladowanie: boolean; blad: unknown; pusto: boolean; pustoTytul: string; children: ReactNode;
}) {
  if (ladowanie) return <LoadingState label="Wczytywanie z systemu głównego…" />;
  if (blad) {
    return <ErrorState error={blad} context={[powodBleduOdczytu(blad), 'Moderacja czyta się wyłącznie z systemu głównego — bez kopii zastępczej.'].filter(Boolean).join(' ')} />;
  }
  if (pusto) return <EmptyState title={pustoTytul} />;
  return <>{children}</>;
}

// ── PODGLĄD ZMIANY ──────────────────────────────────────────────────────────────────────────────

function PodgladZmiany({ p, onZamknij, akcje }: { p: PropozycjaZmianyDania; onZamknij: () => void; akcje?: ReactNode }) {
  const wiersze: [string, string | null, string | null][] = [
    ['Nazwa', p.oldName, p.newName],
    ['Opis', p.oldDescription, p.newDescription],
    ['Składniki', p.oldIngredients, p.newIngredients],
    ['Cena', p.kind === RODZAJ_NOWE_DANIE ? null : cenaZlote(p.oldPrice), cenaZlote(p.newPrice)],
  ];
  const rekoncyliacja = opisRekoncyliacji(p.resolvedBy);
  return (
    <Dialog open onClose={onZamknij} fullWidth maxWidth="md" aria-labelledby="tytul-podgladu">
      <DialogTitle id="tytul-podgladu">
        {etykietaRodzaju(p.kind)}: {p.newName ?? p.mealName}
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {p.restaurantName} · zgłoszono {dataGodzina(p.createdAt)}{p.waitingHours !== null && !p.resolvedAt && !p.approvedAt ? ` · czeka ${etykietaOczekiwania(p.waitingHours)}` : ''}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {p.hasNewerPending ? <Alert severity="info">Dla tego dania czeka nowsza propozycja.</Alert> : null}
          {p.mealDeleted ? <Alert severity="warning">Danie zostało usunięte — zatwierdzenie zmiany jest niemożliwe, propozycję można odrzucić.</Alert> : null}
          <TableContainer>
            <Table size="small" aria-label="Przed i po zmianie">
              <TableHead>
                <TableRow><TableCell /><TableCell sx={{ fontWeight: 700 }}>Przed</TableCell><TableCell sx={{ fontWeight: 700 }}>Po</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {wiersze.map(([pole, przed, po]) => (
                  <TableRow key={pole} selected={(przed ?? '') !== (po ?? '') && p.kind !== RODZAJ_NOWE_DANIE}>
                    <TableCell sx={{ fontWeight: 600 }}>{pole}</TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{przed ?? '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{po ?? '—'}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Zdjęcie</TableCell>
                  <TableCell><Miniatura url={p.oldImageUrl} alt="zdjęcie przed zmianą" /></TableCell>
                  <TableCell><Miniatura url={p.imageUrl} alt="ocenione zdjęcie po zmianie" /></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Box>
            <Typography variant="subtitle2">Ocena AI</Typography>
            <Typography variant="body2">{etykietaDecyzjiAi(p.aiDecision)}{pewnoscProcent(p) ? ` · pewność ${pewnoscProcent(p)}` : ''}{p.aiModel ? ` · model ${p.aiModel}` : ''}</Typography>
            {znacznikiRyzyka(p.aiRiskFlags).length ? <Typography variant="body2">Znaczniki: {znacznikiRyzyka(p.aiRiskFlags).join(', ')}</Typography> : null}
            {p.aiReason ? <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>Powód: {p.aiReason}</Typography> : null}
            {opisZdjecia(p) ? <Typography variant="body2">{opisZdjecia(p)}</Typography> : null}
          </Box>
          {p.resolvedAt || p.resolution ? (
            <Box>
              <Typography variant="subtitle2">Rozstrzygnięcie</Typography>
              <Typography variant="body2">{etykietaRozstrzygniecia(p.resolution)} · status {p.status ?? '—'} · {dataGodzina(p.resolvedAt)}</Typography>
              <Typography variant="body2">Decyzja operatora: {decyzjaOperatora({ resolution: p.resolution, operatorId: p.operatorId, resolvedBy: p.resolvedBy })}</Typography>
              {rekoncyliacja ? <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>{rekoncyliacja}</Typography> : null}
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        {akcje}
        <Button onClick={onZamknij}>Zamknij</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── EKRAN ───────────────────────────────────────────────────────────────────────────────────────

export function ModeracjaMenuPage() {
  const auth = useAuth();
  const api = useAnalyticsApi();
  const queryClient = useQueryClient();
  const mozeDecydowac = auth.has('menuModeration.write');

  const [parametry, setParametry] = useSearchParams();
  const zParam = parametry.get('zakladka') as Zakladka | null;
  const zakladka: Zakladka = zParam && ZAKLADKI.includes(zParam) ? zParam : 'do-potwierdzenia';
  const [strony, setStrony] = useState<Record<'kolejka' | 'ai' | 'historia', number>>({ kolejka: 1, ai: 1, historia: 1 });
  const [filtr, setFiltr] = useState<FiltrHistoriiModeracji>('all');
  const [podglad, setPodglad] = useState<{ p: PropozycjaZmianyDania; zKolejki: boolean } | null>(null);
  const [doOdrzucenia, setDoOdrzucenia] = useState<{ rodzaj: RodzajDecyzjiModeracji; id: number; nazwa: string; restauracja: string } | null>(null);
  const [wToku, setWToku] = useState<string | null>(null);
  const [komunikat, setKomunikat] = useState<{ ton: 'success' | 'info' | 'warning' | 'error'; tekst: string } | null>(null);
  const [pokazAudyt, setPokazAudyt] = useState(false);

  const liczniki = useLicznikiModeracji();
  const legacy = useDaniaDoPotwierdzenia(zakladka === 'do-potwierdzenia');
  const kolejka = useKolejkaModeracji(strony.kolejka, zakladka === 'kolejka');
  const ai = useZatwierdzonePrzezAi(strony.ai, zakladka === 'ai');
  const historia = useHistoriaModeracji(strony.historia, filtr, zakladka === 'historia');
  const audyt = useAudytModeracji(pokazAudyt);

  function wybierzZakladke(z: Zakladka) {
    setParametry((p) => {
      const n = new URLSearchParams(p);
      if (z === 'do-potwierdzenia') n.delete('zakladka'); else n.set('zakladka', z);
      return n;
    }, { replace: true });
  }

  const klucz = (rodzaj: RodzajDecyzjiModeracji, id: number) => `${rodzaj}:${id}`;

  async function decyduj(rodzaj: RodzajDecyzjiModeracji, id: number, zatwierdz: boolean, nazwa: string) {
    if (wToku) return;
    setWToku(klucz(rodzaj, id));
    try {
      const r = zatwierdz ? await api.moderacjaZatwierdz({ rodzaj, id }) : await api.moderacjaOdrzuc({ rodzaj, id });
      if (r.wynik === 'AlreadyResolved') {
        setKomunikat({ ton: 'info', tekst: `„${nazwa}": propozycja była już rozstrzygnięta (np. przez innego operatora albo w Telegramie) — bez zmian.` });
      } else {
        setKomunikat({ ton: 'success', tekst: `„${nazwa}": ${zatwierdz ? 'zatwierdzono' : 'odrzucono'} w systemie głównym.` });
      }
      setPodglad(null);
    } catch (e) {
      const opis = opisBleduOperacji(e);
      setKomunikat({ ton: opis.wynikNieznany ? 'warning' : 'error', tekst: `„${nazwa}": ${opis.tekst}` });
    } finally {
      setWToku(null);
      setDoOdrzucenia(null);
      // Także po błędzie: decyzja mogła się wykonać mimo nieudanej odpowiedzi — stan z systemu głównego.
      await odswiezModeracje(queryClient);
    }
  }

  const przyciskiPropozycji = (p: PropozycjaZmianyDania) => (mozeDecydowac ? (
    <>
      <Button
        size="small" variant="contained"
        disabled={!wolnoZatwierdzic(p) || wToku !== null}
        title={wolnoZatwierdzic(p) ? undefined : 'Danie usunięte — zatwierdzenie niemożliwe'}
        onClick={() => void decyduj('propozycja', p.id, true, p.newName ?? p.mealName ?? String(p.id))}
      >
        {wToku === klucz('propozycja', p.id) ? '…' : 'Zatwierdź'}
      </Button>
      <Button
        size="small" color="error" disabled={wToku !== null}
        onClick={() => setDoOdrzucenia({ rodzaj: 'propozycja', id: p.id, nazwa: p.newName ?? p.mealName ?? String(p.id), restauracja: p.restaurantName ?? '—' })}
      >
        Odrzuć
      </Button>
    </>
  ) : null);

  const L = liczniki.data;

  return (
    <Stack spacing={2.5}>
      <NaglowekEkranu
        tytul="Moderacja menu"
        opis={
          <>
            Dania i zmiany dań czekające na decyzję — te same kolejki i te same operacje co „Menu” w starym panelu admina.
            Po każdej decyzji widok czyta się ponownie z systemu głównego.
          </>
        }
        kartaId="ADMIN-MENU-00"
      />

      {liczniki.error ? (
        <Alert severity="error" role="alert">
          Nie udało się pobrać liczników moderacji. {powodBleduOdczytu(liczniki.error) ?? ''}
        </Alert>
      ) : null}
      {!mozeDecydowac ? <Alert severity="info">Twoje konto widzi kolejki moderacji, ale nie podejmuje decyzji.</Alert> : null}
      {komunikat ? <Alert severity={komunikat.ton} onClose={() => setKomunikat(null)} role="status">{komunikat.tekst}</Alert> : null}

      <Tabs value={zakladka} onChange={(_, z: Zakladka) => wybierzZakladke(z)} variant="scrollable" allowScrollButtonsMobile aria-label="Zakładki moderacji menu">
        <Tab value="do-potwierdzenia" label={<Badge color="primary" badgeContent={L?.doPotwierdzenia ?? 0} max={999}>Do potwierdzenia&nbsp;&nbsp;</Badge>} />
        <Tab
          value="kolejka"
          label={
            <Badge color={L && L.noweDaniaWKolejce > 0 ? 'error' : 'primary'} badgeContent={L ? `${L.kolejka}${L.noweDaniaWKolejce > 0 ? '!' : ''}` : 0} max={999}>
              Do ręcznego zatwierdzenia (AI)&nbsp;&nbsp;
            </Badge>
          }
          title={L && L.noweDaniaWKolejce > 0 ? `W kolejce są nowe dania: ${L.noweDaniaWKolejce}` : undefined}
        />
        <Tab value="ai" label="Zatwierdzone przez AI" />
        <Tab value="historia" label="Historia" />
      </Tabs>

      {/* ── DO POTWIERDZENIA (legacy) ───────────────────────────────────────────────────────── */}
      {zakladka === 'do-potwierdzenia' ? (
        <StanZakladki ladowanie={legacy.isLoading} blad={legacy.error} pusto={(legacy.data?.dania.length ?? 0) === 0} pustoTytul="Brak dań do potwierdzenia">
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Dania do potwierdzenia">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Zdjęcie</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Danie</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Tagi</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Cena</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Ilość</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Restauracja</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Decyzja</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(legacy.data?.dania ?? []).map((d: DanieDoPotwierdzenia) => (
                  <TableRow key={d.id} hover data-danie-id={d.id}>
                    <TableCell><Miniatura url={d.zdjecie} alt={`zdjęcie: ${d.nazwa ?? 'danie'}`} /></TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <div style={{ fontWeight: 600 }}>{d.nazwa ?? '—'}</div>
                      {d.opis ? <Typography variant="caption" component="div">{d.opis}</Typography> : null}
                      {d.skladniki ? <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>Składniki: {d.skladniki}</Typography> : null}
                    </TableCell>
                    <TableCell>{d.tagi.length ? d.tagi.map((t) => <Chip key={t} size="small" label={t} sx={{ mr: 0.5, mb: 0.5 }} />) : '—'}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatGrosze(d.cenaGrosze)}</TableCell>
                    <TableCell align="right">{d.liczbaPosilkow ?? '—'}</TableCell>
                    <TableCell>{d.restauracja.nazwa ?? '—'}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {mozeDecydowac ? (
                        <>
                          <Button size="small" variant="contained" disabled={wToku !== null}
                            onClick={() => void decyduj('danie', d.id, true, d.nazwa ?? String(d.id))}>
                            {wToku === klucz('danie', d.id) ? '…' : 'Zatwierdź'}
                          </Button>
                          <Button size="small" color="error" disabled={wToku !== null}
                            onClick={() => setDoOdrzucenia({ rodzaj: 'danie', id: d.id, nazwa: d.nazwa ?? String(d.id), restauracja: d.restauracja.nazwa ?? '—' })}>
                            Odrzuć
                          </Button>
                        </>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </StanZakladki>
      ) : null}

      {/* ── DO RĘCZNEGO ZATWIERDZENIA (kolejka przeglądu AI) ────────────────────────────────── */}
      {zakladka === 'kolejka' ? (
        <StanZakladki ladowanie={kolejka.isLoading} blad={kolejka.error} pusto={(kolejka.data?.wszystkich ?? 0) === 0} pustoTytul="Brak zmian czekających na ręczne zatwierdzenie">
          {kolejka.data ? (
            <Stack spacing={1}>
              <Typography variant="body2" data-podsumowanie-kolejki>
                Oczekujące: <strong>{kolejka.data.wszystkich}</strong> · bez powiadomienia: <strong>{kolejka.data.bezPowiadomienia ?? 0}</strong>
                {' '}· z usuniętym daniem: <strong>{kolejka.data.zUsunietymDaniem ?? 0}</strong>
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                <Table size="small" aria-label="Zmiany do ręcznego zatwierdzenia">
                  <TableHead>
                    <TableRow>
                      {['Czeka', 'Restauracja', 'Danie', 'Rodzaj / zmiana', 'Cena przed → po', 'Zdjęcie', 'Powód przeglądu', 'Stan', 'Decyzja'].map((n) => (
                        <TableCell key={n} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{n}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {kolejka.data.pozycje.map((p) => {
                      const kat = kategoriaPowodu(p);
                      return (
                        <TableRow key={p.id} hover data-propozycja-id={p.id} sx={p.mealDeleted ? { opacity: 0.75 } : undefined}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <div>{etykietaOczekiwania(p.waitingHours)}</div>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{dataGodzina(p.createdAt)}</Typography>
                          </TableCell>
                          <TableCell>{p.restaurantName ?? '—'}</TableCell>
                          <TableCell><Danie p={p} /></TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}><RodzajZmiany p={p} /></TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{cenaPrzedPo(p)}</TableCell>
                          <TableCell>
                            <Miniatura url={p.imageUrl} alt="ocenione zdjęcie dania" />
                            {p.imageAssessmentLabel ? <Typography variant="caption">{p.imageAssessmentLabel}</Typography> : null}
                          </TableCell>
                          <TableCell sx={{ minWidth: 220 }}>
                            <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                              <Chip size="small" color={KOLOR_KATEGORII[kat.kod]} label={kat.etykieta} data-kategoria={kat.kod} />
                              {pewnoscProcent(p) ? <Typography variant="caption">pewność {pewnoscProcent(p)}</Typography> : null}
                            </Stack>
                            {czyProblemZdjecia(p) ? <Typography variant="caption" component="div" sx={{ color: 'error.main' }}>{opisZdjecia(p)}</Typography> : null}
                            <Typography variant="caption" component="div">{opisPowodu(p)}</Typography>
                            {p.aiReason ? <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>{p.aiReason}</Typography> : null}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <div>Danie: <strong>{etykietaStanuDania(p)}</strong></div>
                            <Typography variant="caption" component="div">Powiadomienie: {etykietaPowiadomienia(p)}</Typography>
                            {p.hasNewerPending ? <Typography variant="caption" component="div">jest nowsza propozycja</Typography> : null}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Stack spacing={0.5} sx={{ alignItems: 'flex-start' }}>
                              {przyciskiPropozycji(p)}
                              <Button size="small" onClick={() => setPodglad({ p, zKolejki: true })}>Podgląd</Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Stronicowanie strona={strony.kolejka} dane={kolejka.data} onStrona={(s) => setStrony((x) => ({ ...x, kolejka: s }))} />
            </Stack>
          ) : null}
        </StanZakladki>
      ) : null}

      {/* ── ZATWIERDZONE PRZEZ AI ──────────────────────────────────────────────────────────────── */}
      {zakladka === 'ai' ? (
        <StanZakladki ladowanie={ai.isLoading} blad={ai.error} pusto={(ai.data?.wszystkich ?? 0) === 0} pustoTytul="Brak dań zatwierdzonych przez AI">
          {ai.data ? (
            <Stack spacing={1}>
              <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                <Table size="small" aria-label="Zatwierdzone przez AI">
                  <TableHead>
                    <TableRow>
                      {['Zatwierdzono', 'Restauracja', 'Danie', 'Rodzaj / zmiana', 'Cena przed → po', 'Zdjęcie', 'Wynik AI / powód', 'Podgląd'].map((n) => (
                        <TableCell key={n} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{n}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ai.data.pozycje.map((p) => (
                      <TableRow key={p.id} hover data-propozycja-id={p.id}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{dataGodzina(p.approvedAt ?? p.resolvedAt)}</TableCell>
                        <TableCell>{p.restaurantName ?? '—'}</TableCell>
                        <TableCell><Danie p={p} /></TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}><RodzajZmiany p={p} /></TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{cenaPrzedPo(p)}</TableCell>
                        <TableCell><Miniatura url={p.imageUrl} alt="ocenione zdjęcie dania" /></TableCell>
                        <TableCell sx={{ minWidth: 200 }}>
                          {pewnoscProcent(p) ? <Typography variant="caption" component="div">pewność {pewnoscProcent(p)}</Typography> : null}
                          {p.aiReason ? <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>{p.aiReason}</Typography> : null}
                        </TableCell>
                        <TableCell><Button size="small" onClick={() => setPodglad({ p, zKolejki: false })}>Podgląd</Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Stronicowanie strona={strony.ai} dane={ai.data} onStrona={(s) => setStrony((x) => ({ ...x, ai: s }))} />
            </Stack>
          ) : null}
        </StanZakladki>
      ) : null}

      {/* ── HISTORIA ─────────────────────────────────────────────────────────────────────────── */}
      {zakladka === 'historia' ? (
        <Stack spacing={1.5}>
          <TextField
            select size="small" label="Filtr historii" value={filtr} sx={{ maxWidth: 360 }}
            onChange={(e) => { setFiltr(e.target.value as FiltrHistoriiModeracji); setStrony((x) => ({ ...x, historia: 1 })); }}
          >
            {FILTRY_HISTORII.map((f) => <MenuItem key={f.klucz} value={f.klucz}>{f.etykieta}</MenuItem>)}
          </TextField>
          <StanZakladki ladowanie={historia.isLoading} blad={historia.error} pusto={(historia.data?.wszystkich ?? 0) === 0} pustoTytul="Brak rozstrzygniętych propozycji dla tego filtra">
            {historia.data ? (
              <Stack spacing={1}>
                <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                  <Table size="small" aria-label="Historia moderacji">
                    <TableHead>
                      <TableRow>
                        {['Rozstrzygnięto', 'Restauracja', 'Danie', 'Rodzaj / zmiana', 'Cena przed → po', 'Rozstrzygnięcie', 'Decyzja AI', 'Decyzja operatora', 'Podgląd'].map((n) => (
                          <TableCell key={n} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{n}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {historia.data.pozycje.map((p) => (
                        <TableRow key={p.id} hover data-propozycja-id={p.id}>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{dataGodzina(p.resolvedAt)}</TableCell>
                          <TableCell>{p.restaurantName ?? '—'}</TableCell>
                          <TableCell><Danie p={p} /></TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}><RodzajZmiany p={p} /></TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{cenaPrzedPo(p)}</TableCell>
                          <TableCell sx={{ minWidth: 180 }}>{etykietaRozstrzygniecia(p.resolution)}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{etykietaDecyzjiAi(p.aiDecision)}{pewnoscProcent(p) ? ` · ${pewnoscProcent(p)}` : ''}</TableCell>
                          <TableCell sx={{ minWidth: 180 }}>{decyzjaOperatora({ resolution: p.resolution, operatorId: p.operatorId, resolvedBy: p.resolvedBy })}</TableCell>
                          <TableCell><Button size="small" onClick={() => setPodglad({ p, zKolejki: false })}>Podgląd</Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Stronicowanie strona={strony.historia} dane={historia.data} onStrona={(s) => setStrony((x) => ({ ...x, historia: s }))} />
              </Stack>
            ) : null}
          </StanZakladki>
        </Stack>
      ) : null}

      <Box>
        <Button size="small" onClick={() => setPokazAudyt((v) => !v)}>
          {pokazAudyt ? 'Ukryj decyzje z Control Center' : 'Pokaż decyzje podjęte z Control Center'}
        </Button>
        <Collapse in={pokazAudyt} unmountOnExit>
          <Paper variant="outlined" sx={{ p: 2, mt: 1, overflowX: 'auto' }}>
            {audyt.data?.wpisy.length ? (
              <Table size="small" aria-label="Decyzje moderacji z Control Center">
                <TableHead>
                  <TableRow><TableCell>Kiedy</TableCell><TableCell>Kto</TableCell><TableCell>Decyzja</TableCell><TableCell>Pozycja</TableCell><TableCell>Wynik</TableCell></TableRow>
                </TableHead>
                <TableBody>
                  {audyt.data.wpisy.map((w, i) => (
                    <TableRow key={`${w.czas}-${i}`}>
                      <TableCell>{new Date(w.czas).toLocaleString('pl-PL', { timeZone: 'Europe/Warsaw' })}</TableCell>
                      <TableCell>{w.aktor.email ?? '—'}</TableCell>
                      <TableCell>{w.operacja === 'ZATWIERDZ_DANIE' ? 'Zatwierdź' : 'Odrzuć'}</TableCell>
                      <TableCell>{String(w.encja.rodzaj ?? '')} #{String(w.encja.id ?? '')}</TableCell>
                      <TableCell>{w.wynik}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : audyt.isLoading ? <LoadingState /> : (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Brak decyzji z Control Center od uruchomienia usługi.</Typography>
            )}
          </Paper>
        </Collapse>
      </Box>

      {podglad ? (
        <PodgladZmiany p={podglad.p} onZamknij={() => setPodglad(null)} akcje={podglad.zKolejki ? przyciskiPropozycji(podglad.p) : undefined} />
      ) : null}

      <Dialog open={doOdrzucenia !== null} onClose={() => (wToku ? undefined : setDoOdrzucenia(null))} aria-labelledby="tytul-odrzucenia">
        {doOdrzucenia ? (
          <>
            <DialogTitle id="tytul-odrzucenia">{doOdrzucenia.rodzaj === 'propozycja' ? 'Odrzucić zmianę dania?' : 'Odrzucić danie?'}</DialogTitle>
            <DialogContent dividers>
              <Typography>
                <strong>{doOdrzucenia.nazwa}</strong> · {doOdrzucenia.restauracja}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                {doOdrzucenia.rodzaj === 'propozycja'
                  ? 'Ta sama operacja co „Odrzuć” w kolejce przeglądu starego panelu — propozycja zostaje rozstrzygnięta jako odrzucona.'
                  : 'Ta sama operacja co „Odrzuć” w zakładce „Do potwierdzenia” starego panelu.'}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDoOdrzucenia(null)} disabled={wToku !== null}>Anuluj</Button>
              <Button variant="contained" color="error" disabled={wToku !== null}
                onClick={() => void decyduj(doOdrzucenia.rodzaj, doOdrzucenia.id, false, doOdrzucenia.nazwa)}>
                {wToku ? 'Odrzucanie…' : 'Odrzuć'}
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>
    </Stack>
  );
}

export default ModeracjaMenuPage;
